import { AgPublicEventType, RowEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { KeyboardEvent, RefObject, useMemo } from "react";
import { ContextAction, ModifierKeys } from "./context-action";

export const createShortcutHandler = (...actions: ContextAction[]) => {
  const shortcutLookup = createShortcutLookup(actions);
  return (event: KeyboardEvent) => {
    handleEvent(shortcutLookup, event);
  };
};

export const createGridShortcutHandler = (...actions: ContextAction[]) => {
  const shortcutLookup = createShortcutLookup(actions);
  return (rowEvent: RowEvent<AgPublicEventType>) => {
    const event = rowEvent.event as KeyboardEvent | null | undefined;
    if (event != null && !rowEvent.api.getEditingCells().length) {
      handleEvent(shortcutLookup, event);
    }
  };
};

export const useGridSelectAllAction = (gridApiRef: RefObject<AgGridReact>) => {
  return useMemo(
    () => new ContextAction({
      action: () => {
        const isServerSideGrid = gridApiRef.current?.api?.getGridOption("rowModelType") === "serverSide";
        if (!isServerSideGrid) gridApiRef.current?.api?.deselectAll();
        gridApiRef.current?.api?.selectAllFiltered();
      },
      shortcut: { modifiers: ["ctrl"], key: "a" },
      textKey: "toolbar.selectAll",
    }),
    [gridApiRef],
  );
};

type ShortcutLookup = Map<string, ContextActionWithShortcut[]>;
type ContextActionWithShortcut = ContextAction & Required<Pick<ContextAction, "shortcut">>;
const createShortcutLookup = (actions: ContextAction[]): ShortcutLookup => {
  return (actions.filter(({ shortcut }) => shortcut) as ContextActionWithShortcut[]).reduce(
    (acc, action) => acc.set(action.shortcut.key.toUpperCase(), [...(acc.get(action.shortcut.key.toUpperCase()) ?? []), action]),
    new Map<string, ContextActionWithShortcut[]>(),
  );
};

function handleEvent(shortcutLookup: ShortcutLookup, event: KeyboardEvent) {
  shortcutLookup.get(event.key.toUpperCase())
    ?.forEach(({ action, shortcut: { modifiers } }) => {
      if (eventModifiersMatch(event, modifiers)) {
        event.stopPropagation();
        event.preventDefault();
        action();
      }
    });
}

function eventModifiersMatch(event: KeyboardEvent, modifiers: ModifierKeys[]) {
  const eventCtrlKey = event.ctrlKey || (navigator.userAgent.includes("Mac OS X") && event.metaKey);
  return event.altKey === modifiers.includes("alt") && eventCtrlKey === modifiers.includes("ctrl") && event.shiftKey === modifiers.includes("shift");
}
